import React, { useEffect, useRef } from 'react';
import { createChart, AreaSeries } from 'lightweight-charts';

/**
 * Tiny, no-chrome sparkline using the same `lightweight-charts` engine as the
 * premium candlestick chart — so we have zero new bundle dependencies. Renders
 * a single area series with no axes, no toolbar, no crosshair, no tooltip.
 *
 * Props:
 *   data:   [{ date: 'YYYY-MM-DD', value: number }]  REQUIRED
 *   color:  stroke color (default purple-500 #8b5cf6)
 *   height: pixel height (default 36)
 */
const MiniSparkline = ({ data = [], color = '#8b5cf6', height = 36 }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !data?.length) return undefined;

    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: { background: { color: 'transparent' }, textColor: 'transparent' },
      grid: { vertLines: { visible: false }, horzLines: { visible: false } },
      timeScale: { visible: false, borderVisible: false },
      rightPriceScale: { visible: false, borderVisible: false },
      leftPriceScale: { visible: false },
      handleScale: false,
      handleScroll: false,
      crosshair: { mode: 0, vertLine: { visible: false }, horzLine: { visible: false } },
      localization: { locale: 'en-US' },
    });

    const series = chart.addSeries(AreaSeries, {
      lineColor: color,
      topColor: color + '55',
      bottomColor: color + '00',
      lineWidth: 1.5,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: false,
    });

    series.setData(
      data
        .filter((d) => d && d.value != null)
        .map((d) => ({ time: d.date, value: Number(d.value) })),
    );

    chart.timeScale().fitContent();

    return () => {
      try {
        chart.remove();
      } catch (_) {
        /* already removed */
      }
    };
  }, [data, color]);

  if (!data?.length) return null;

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: `${height}px` }}
      data-testid="mini-sparkline"
    />
  );
};

export default MiniSparkline;
